import { observer } from 'mobx-react-lite'
import { Tabs } from '@gate/gui'
import { useTranslation } from 'next-i18next'
import { useRequest, useUpdateEffect } from 'ahooks'
import { useRouter } from 'next/router'
import { getHeatmap, getPlatePrice, getTopCoinDrawing, getPlate } from 'api/price'
import React, { useCallback, useMemo, useState } from 'react'
import { eventTracker } from '@/utils/collectEvent'
import type { PerformChartData } from 'api-price'
import { getFundraisingGraph } from 'api/fundraising'
import { Box, Skeleton, Flex } from '@mantine/core'
import type { FundraisingGraphChartData, FundraisingSeries } from 'api-fundraising'
import { EnumCoinHeatTimeTab, EnumCoinLineTimeTab, EnumCoinChartsTab, MONTH_FORMATE } from '@/constants/index'
import { PriceChartStyled, PriceHeartChartSkeletonContainerDiv, PriceChartTimeTabStyled, ThermalMapTip } from './style'
import BaseSuspense from '@/components/BaseSuspense'
import FundingSearch from './Components/FundingSearch'
import FundingDate, { FundingDateValueType } from './Components/FundingDate'
import PriceHotTab, { PriceHotValueType } from './Components/PriceHotTab'
import PriceTrendTab, { PriceTrendValueType } from './Components/PriceTrendTab'
import dayjs from 'dayjs'
const HeatChart = React.lazy(() => import('./HeatChart'))
const TrendChart = React.lazy(() => import('./TrendChart'))
const PerformChart = React.lazy(() => import('./PerformChart'))
const FundraisingChart = React.lazy(() => import('./FundraisingChart'))

export const recordChartTabsMap: Record<string, any> = {
  [EnumCoinChartsTab.HEAT]: 'heat',
  [EnumCoinChartsTab.TREND]: 'sectors',
  [EnumCoinChartsTab.PERFORM]: 'top performers',
}

const PriceChart = () => {
  const { t } = useTranslation()
  const { locale } = useRouter()

  // tabs Value
  const [tabsValue, setTabsValue] = useState<string & EnumCoinChartsTab[keyof EnumCoinChartsTab]>(
    EnumCoinChartsTab['HEAT']
  )

  // 热力图 | loading
  const [heatLoading, setHeatLoading] = useState<boolean>(true)
  // 趋势图 | loading
  const [trendLoading, setTrendLoading] = useState<boolean>(true)
  // 代币表现图 | loading
  const [performLoading, setPerformLoading] = useState<boolean>(true)

  // 机构投资图 | loading
  const [fundingLoading, setFundingLoading] = useState<boolean>(true)

  // 代币表现图 | 二级菜单tabs
  const [plateSelector, setPlateSelector] = useState<string>('')

  // 热力图时间选项 tabs
  const [priceHeatChartTimeTab, setPriceHeatChartTimeTab] = useState<PriceHotValueType>(EnumCoinHeatTimeTab['ONE_DAY'])

  // 趋势图时间选项 tabs
  const [priceTrendChartTimeTab, setPriceTrendChartTimeTab] = useState<PriceTrendValueType>(
    EnumCoinLineTimeTab['ONE_YEAR']
  )

  // 代币表现图 tabs
  const [pricePerformChartTimeTab, setPricePerformChartTimeTab] = useState<PriceTrendValueType>(
    EnumCoinLineTimeTab['ONE_DAY']
  )

  // 投资机构tab 机构筛选
  const [fundingInvestVal, setFundingInvestVal] = useState<string[]>([])

  // 投资机构tab 时间筛选
  const [fundingDateVal, setFundingDateVal] = useState<[Date | null, Date | null]>([
    dayjs().subtract(1, 'year').toDate(),
    dayjs().toDate(),
  ])

  // 热力图
  const { run: runHeatMap, data: heatMap = [] } = useRequest(getHeatmap, {
    manual: false,
    onFinally() {
      setHeatLoading(false)
    },
  })

  //  板块折线图
  const { run: runLineMap, data: lineMap = [] } = useRequest(getPlatePrice, {
    manual: false,
    onFinally() {
      setTrendLoading(false)
    },
  })

  //  趋势图 | 获取板块
  const { data: plateData = [] } = useRequest(getPlate, {
    manual: false,
    onSuccess(res) {
      const resFirstId = res[0]?.plate_unique_id
      setPlateSelector(resFirstId)
      runPerformMap({ time_span: pricePerformChartTimeTab, kind_name: resFirstId })
    },
  })

  //  代币表现图
  const { run: runPerformMap, data: performMap = [] } = useRequest(getTopCoinDrawing, {
    manual: true,
    onFinally() {
      setPerformLoading(false)
    },
  })

  // 机构投资图
  const { run: runFundingMap, data: fundingData = { xData: [], yData: [], select: [] } } = useRequest(
    getFundraisingGraph,
    {
      manual: false,
      defaultParams: [
        {
          date_start: dayjs(fundingDateVal?.[0])?.format(MONTH_FORMATE),
          date_end: dayjs(fundingDateVal?.[1])?.format(MONTH_FORMATE),
          lang: locale ?? 'en',
        },
      ],
      onSuccess(data) {
        setFundingInvestVal(data?.select)
      },
      onFinally() {
        setFundingLoading(false)
      },
    }
  )

  useUpdateEffect(() => {
    runFundingMap({
      date_start: dayjs(fundingDateVal?.[0])?.format(MONTH_FORMATE),
      date_end: dayjs(fundingDateVal?.[1])?.format(MONTH_FORMATE),
      lang: locale ?? 'en',
    })
  }, [locale])

  /**
   * @description: 机构投资图数据
   * @param {*} useMemo
   * @return {*}
   */
  const currentFundingData = useMemo(() => {
    const { xData, yData, select } = fundingData
    // 记录当前索引
    const dataIndexArray: number[] = []

    const newYData: FundraisingSeries[] = []

    xData.forEach((item: string, index: number) => {
      if (fundingInvestVal?.includes(item)) {
        dataIndexArray.push(index)
      }
    })
    yData.forEach((item: FundraisingSeries) => {
      newYData.push({
        ...item,
        counting: item?.counting?.filter((_, curIndex) => dataIndexArray.includes(curIndex)),
      })
    })

    return {
      xData: fundingInvestVal,
      yData: newYData,
      select,
    }
  }, [fundingInvestVal, fundingData])

  /**
   * @description: 一级tab change
   * @param {string} tab
   * @return {*}
   */
  const onTabChange = (tab: string & EnumCoinChartsTab[keyof EnumCoinChartsTab]): void => {
    setTabsValue(tab)
    eventTracker('price_overview_click', {
      tab_name: recordChartTabsMap[tab],
    })
  }

  /**
   * @description: 代币表现图 tabs change
   * @return {*}
   */
  const handlePlateChange = useCallback(
    (val: string): void => {
      setPlateSelector(val)
      runPerformMap({ time_span: pricePerformChartTimeTab, kind_name: val })
    },
    [pricePerformChartTimeTab, runPerformMap]
  )

  /**
   * @description: 机构投资图 time选择
   * @param {FundingDateValueType} val
   * @return {*}
   */
  const handleSelectFundingDateVal = (val: FundingDateValueType): void => {
    setFundingDateVal(val)

    const startTimeVal = val[0]
    const endTimeVal = val[1]

    if (startTimeVal && endTimeVal) {
      const startTime = startTimeVal ? dayjs(startTimeVal).format(MONTH_FORMATE) : ''
      const endTime = startTimeVal ? dayjs(endTimeVal).format(MONTH_FORMATE) : ''
      runFundingMap({ date_start: startTime, date_end: endTime, lang: locale ?? 'en' })
    }
  }

  const directionAlignTabs = useMemo((): Record<string, any> => {
    if (tabsValue === EnumCoinChartsTab?.INVESTMENT) {
      return {
        align: { base: 'flex-start', lg: 'center' },
        direction: { base: 'column', lg: 'row' },
        mt: { base: 16, lg: 0 },
      }
    }

    return {
      align: { base: 'flex-start', sm: 'center' },
      direction: { base: 'column', sm: 'row' },
      mt: { base: 16, sm: 0 },
    }
  }, [tabsValue])

  return (
    <PriceChartStyled data-fundraising={String(tabsValue === EnumCoinChartsTab.INVESTMENT)} id="price-width-id">
      <Tabs type="primary" w={'100%'} size={'md'} defaultValue={EnumCoinChartsTab.HEAT} onTabChange={onTabChange}>
        <Flex
          wrap={'nowrap'}
          justify="space-between"
          align={directionAlignTabs?.align}
          direction={directionAlignTabs?.direction}
        >
          <Tabs.List className="price-fundraising-tabs">
            {Object.values(EnumCoinChartsTab).map(key => (
              <Tabs.Tab key={key} value={key}>
                <Box component="h2" className="font-700" fz={{ base: '16px', sm: '20px' }}>
                  {t(`${key}`, { ns: key === EnumCoinChartsTab.INVESTMENT ? 'fundraising' : 'price' })}
                </Box>
              </Tabs.Tab>
            ))}
          </Tabs.List>

          {/* 热力图 ｜ tabs */}
          <Flex mt={directionAlignTabs?.mt}>
            <PriceHotTab
              isShow={tabsValue === EnumCoinChartsTab['HEAT']}
              priceHeatChartTimeTab={priceHeatChartTimeTab}
              handlePriceHotTabChange={val => {
                setPriceHeatChartTimeTab(val)
                runHeatMap({ time: val })
              }}
            />
            {/* 代币表现图 | tabs */}
            <PriceTrendTab
              isShow={tabsValue === EnumCoinChartsTab['PERFORM']}
              priceTrendValue={pricePerformChartTimeTab}
              handlePriceTrendChange={val => {
                setPricePerformChartTimeTab(val)
                runPerformMap({ time_span: val, kind_name: plateSelector })
              }}
            />
            {/* 机构投资图  | tabs */}
            <PriceChartTimeTabStyled isShow={tabsValue === EnumCoinChartsTab?.INVESTMENT}>
              <Flex gap={12} direction={{ base: 'column', sm: 'row' }}>
                <FundingSearch
                  handleFundingSelect={val => setFundingInvestVal(val)}
                  fundingInvestVal={fundingInvestVal}
                  fundSearchList={fundingData?.select}
                />
                <FundingDate fundingDateVal={fundingDateVal} handleSelectFundingDateVal={handleSelectFundingDateVal} />
              </Flex>
            </PriceChartTimeTabStyled>
            {/* 趋势图 | tabs */}
            <PriceTrendTab
              isShow={tabsValue === EnumCoinChartsTab['TREND']}
              priceTrendValue={priceTrendChartTimeTab}
              handlePriceTrendChange={val => {
                setPriceTrendChartTimeTab(val)
                runLineMap({ time_span: val })
              }}
            />
          </Flex>
        </Flex>

        {/*  热力图*/}
        <Tabs.Panel value={EnumCoinChartsTab.HEAT}>
          <BaseSuspense loading={heatLoading} fallback={<PriceHeartChartSkeleton />}>
            <HeatChart data={heatMap} />
          </BaseSuspense>
          <ThermalMapTip>{t('P.DataReferenceTip')}</ThermalMapTip>
        </Tabs.Panel>

        {/*  趋势图*/}
        <Tabs.Panel value={EnumCoinChartsTab.TREND}>
          <BaseSuspense loading={trendLoading} fallback={<PriceTrendAndPerformChartSkeleton />}>
            <TrendChart data={lineMap} lineTab={priceTrendChartTimeTab} />
          </BaseSuspense>
        </Tabs.Panel>

        {/*  代币表现图*/}
        <Tabs.Panel value={EnumCoinChartsTab.PERFORM}>
          <BaseSuspense loading={performLoading} fallback={<PriceTrendAndPerformChartSkeleton />}>
            <PerformChart
              data={performMap as { [key: string]: PerformChartData[] }}
              lineTab={pricePerformChartTimeTab}
              plateData={plateData}
              tabsValue={tabsValue}
              onPlateChange={handlePlateChange}
              plateSelector={plateSelector}
            />
          </BaseSuspense>
        </Tabs.Panel>

        {/* 机构投资图 */}
        <Tabs.Panel value={EnumCoinChartsTab.INVESTMENT}>
          <BaseSuspense loading={fundingLoading} fallback={<PriceTrendAndPerformChartSkeleton />}>
            <FundraisingChart fundingData={currentFundingData as FundraisingGraphChartData} />
          </BaseSuspense>
        </Tabs.Panel>
      </Tabs>
    </PriceChartStyled>
  )
}

/**
 * @description: 热力图 | 骨架屏
 * @param {*} React
 * @return {*}
 */
const PriceHeartChartSkeleton = (): React.ReactElement => {
  return (
    <PriceHeartChartSkeletonContainerDiv>
      <Skeleton w={'100%'} h={410} />
      <Skeleton w={'100%'} h={410} />
      <Skeleton w={'100%'} h={410} />
    </PriceHeartChartSkeletonContainerDiv>
  )
}

/**
 * @description: 趋势&表现图 | 骨架屏
 * @param {*} React
 * @return {*}
 */
const PriceTrendAndPerformChartSkeleton = (): React.ReactElement => {
  return (
    <Flex direction="column" mt={24}>
      <Skeleton w={'100%'} h={352} />
      <Flex justify="center" mt={44}>
        <Skeleton w={'98%'} h={14} />
      </Flex>
    </Flex>
  )
}

export default observer(PriceChart)
